import {
  Building2,
  CheckSquare,
  CircleHelp,
  ClipboardList,
  FileCheck,
  FileText,
  Gavel,
  LayoutDashboard,
  Layers,
  Leaf,
  ListTodo,
  MessagesSquare,
  PencilRuler,
  PieChart,
  Settings,
  ShieldCheck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react'

import type { FeatureFlagKey } from '@/types/featureFlags'

export interface NavigationItem {
  label: string
  to: string
  icon: LucideIcon
  /**
   * Modulet, punktet hører til — er det slået fra for organisationen, vises
   * punktet ikke.
   *
   * Projektfanerne gjorde det allerede (se `TAB_MODULES` i
   * `ProjectDetailPage`); menuen gjorde ikke, og de to punkter herunder stod
   * derfor og førte til en fejlbesked hos de organisationer, der havde fravalgt
   * dem. Det er pynt og ikke sikkerhed: serveren spærrer uafhængigt.
   */
  module?: FeatureFlagKey
  /**
   * Ruter der endnu ikke er implementeret, vises som "på vej".
   *
   * Ingen bruger den længere — alle punkter fører nu et sted hen. Feltet bliver
   * stående, fordi det er den eneste ærlige måde at tilføje et menupunkt for
   * noget, der ikke er bygget endnu: alternativet er en knap, der ikke gør
   * noget, og det forbyder masterprompten.
   */
  comingSoon?: boolean
}

export interface NavigationGroup {
  /** Bruges til `aria-controls` og som nøgle. */
  id: string
  label: string
  icon: LucideIcon
  /** Vises under overskriften, når gruppen er foldet ud. */
  description: string
  items: NavigationItem[]
}

/**
 * Hovednavigationen. Ét sted for både sidebar og mobilmenu, så de aldrig
 * kommer ud af trit.
 *
 * Alle punkter fører nu et sted hen — se `comingSoon` for, hvad der sker med
 * dem, der en dag ikke gør.
 */
export const PRIMARY_NAVIGATION: NavigationItem[] = [
  { label: 'Overblik', to: '/app/dashboard', icon: LayoutDashboard },
  { label: 'Projekter', to: '/app/projects', icon: Building2 },
  { label: 'Mine godkendelser', to: '/app/godkendelser', icon: FileCheck },
]

/**
 * Modulerne på tværs af alle projekter.
 *
 * De fleste af dem findes også som en fane **inde på det enkelte projekt** —
 * det er samlingen, der ligger her: ét dokumentarkiv, én opgaveliste, ét
 * udbudsoverblik for hele organisationen. De lå før spredt ud i menuen med
 * hvert sit "på vej", hvilket læste som om modulerne ikke fandtes.
 *
 * De tværgående sider er lister og ikke arbejdsflader. Man opretter ikke et
 * dokument, en opgave eller et udbud herfra: det skal ned i én bestemt sag, og
 * en knap, der først skulle spørge hvilken, ville være et omvej rundt om det
 * sted, svaret allerede er givet. Til gengæld kan man åbne alt herfra.
 *
 * **Rapporter** er undtagelsen: den findes kun her og har ingen projektfane.
 * En rapport laves for ét projekt, men den læses på tværs — man har ni sager og
 * skal kunne finde den nyeste mangelliste uden først at vælge en sag.
 *
 * Gruppen er foldet sammen som udgangspunkt.
 */
export const CROSS_PROJECT_NAVIGATION: NavigationGroup = {
  id: 'paa-tvaers',
  label: 'På tværs af projekter',
  icon: Layers,
  description: 'Samler alle projekter ét sted. Modulerne findes også på hvert projekt.',
  items: [
    { label: 'Dokumenter', to: '/app/documents', icon: FileText },
    { label: 'Tegninger', to: '/app/drawings', icon: PencilRuler },
    { label: 'Udbud', to: '/app/tenders', icon: Gavel },
    { label: 'Opgaver', to: '/app/tasks', icon: ListTodo },
    { label: 'Checklister', to: '/app/quality', icon: CheckSquare },
    { label: 'Kommunikation', to: '/app/communication', icon: MessagesSquare },
    { label: 'Bæredygtighed', to: '/app/sustainability', icon: Leaf, module: 'sustainability-module' },
    { label: 'Drift og vedligehold', to: '/app/operations', icon: Wrench, module: 'operations-module' },
    { label: 'Rapporter', to: '/app/reports', icon: PieChart },
  ],
}

/** Kontoen og organisationen. Står nederst, adskilt fra det daglige arbejde. */
export const SECONDARY_NAVIGATION: NavigationItem[] = [
  { label: 'Medlemmer', to: '/app/settings/members', icon: Users },
  { label: 'Indstillinger', to: '/app/settings/profile', icon: Settings },
  { label: 'Hjælp', to: '/app/hjaelp', icon: CircleHelp },
]

/**
 * Platformadministrationen.
 *
 * Står for sig selv og indgår hverken i menuens faste lister eller i
 * kommandopaletten: punktet findes kun for de ganske få brugere, der har
 * adgang, og de lister her er fælles for alle. Sidebaren lægger det ind, når
 * brugeren har `isPlatformAdmin`.
 */
export const PLATFORM_NAVIGATION_ITEM: NavigationItem = {
  label: 'Platform',
  to: '/app/platform',
  icon: ShieldCheck,
}

/**
 * Alle punkter i én flad liste. Kommandopaletten skal kunne finde et punkt,
 * uanset hvilken gruppe det ligger i — en menu, der er foldet sammen, må ikke
 * skjule noget for søgningen.
 */
export const ALL_NAVIGATION_ITEMS: NavigationItem[] = [
  ...PRIMARY_NAVIGATION,
  ...CROSS_PROJECT_NAVIGATION.items,
  ...SECONDARY_NAVIGATION,
]

/**
 * De fire vigtigste områder, som vises i bundnavigationen på mobil.
 *
 * "Mangler" og ikke "Checklister" som i sidebaren: på en byggeplads er det
 * manglerne, man slår op på en telefon, og kvalitetssiden åbner netop på dem.
 */
export const MOBILE_NAVIGATION: NavigationItem[] = [
  { label: 'Overblik', to: '/app/dashboard', icon: LayoutDashboard },
  { label: 'Dokumenter', to: '/app/documents', icon: FileText },
  { label: 'Opgaver', to: '/app/tasks', icon: ListTodo },
  { label: 'Mangler', to: '/app/quality', icon: ClipboardList },
]
